import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import apiClient from "../services/api";
import { downloadCsvBlob } from "../services/llmUsageApi";
import DownloadIcon from "../components/icons/DownloadIcon";
import CloudUploadIcon from "../components/icons/CloudUploadIcon";
import EditIcon from "../components/icons/EditIcon";
import { useHotel } from "../contexts/HotelContext";

const CSV_HEADERS = [
  "nip",
  "nazwa",
  "adres",
  "identyfikatorGminy",
  "formaPrawna",
  "formaPrawnaInna",
  "wielkoscPodmiotu",
  "pkd",
  "pkdWersja",
  "dataUtworzenia",
  "data",
  "stanowisko",
  "telefon",
  "imieNazwisko",
  // Page 1 right column (wnioskodawca)
  "nipWnioskodawcy",
  "nazwaWnioskodawcy",
  "adresWnioskodawcy",
  // Section A.9 - Relationships
  "sekcja9a",
  "sekcja9b",
  "sekcja9c",
  "sekcja9d",
  "sekcja9e",
  // Section A.9 conditional
  "sekcja9NipPowiazanych",
  "sekcja9WartoscPomocy",
  // Section A.10 - Mergers/splits
  "sekcja10a",
  "sekcja10b",
  "sekcja10c",
  "sekcja10d",
  // Section A.10a/b conditional
  "sekcja10abNipPolaczonych",
  "sekcja10abWartoscPomocy",
  // Section A.10c/d conditional
  "sekcja10cdNipPrzedPodzialem",
  "sekcja10cdWartoscPomocy",
  "sekcja10cdWartoscPrzedPodzialem",
  "sekcja10cdKapitalPrzed",
  "sekcja10cdKapitalMoment",
  // Section B - Economic situation
  "sekcjaB1",
  "sekcjaB2",
  "sekcjaB3a",
  "sekcjaB3b",
  "sekcjaB3c",
  "sekcjaB3d",
  "sekcjaB3e",
  "sekcjaB3f",
  "sekcjaB3g",
  "sekcjaB3h",
  "sekcjaB3i",
  // Section B.3i conditional
  "sekcjaB3iOpis",
  // Section C - Exclusions
  "sekcjaC1a",
  "sekcjaC1b",
  "sekcjaC2",
  "sekcjaC3",
  // Section C.3 conditional
  "sekcjaC3Opis",
  // Section D - Aid received
  "sekcjaD1",
  "sekcjaD2",
  // Section D.4a
  "opisPrzedsiewziecia",
  // Section D.3 table rows (4 rows x 6 columns = 24 columns)
  "d3Row1DzienUdzielenia",
  "d3Row1PodmiotUdzielajacy",
  "d3Row1PodstawaPrawna",
  "d3Row1Przeznaczenie",
  "d3Row1FormaPomocy",
  "d3Row1WartoscBrutto",
  "d3Row2DzienUdzielenia",
  "d3Row2PodmiotUdzielajacy",
  "d3Row2PodstawaPrawna",
  "d3Row2Przeznaczenie",
  "d3Row2FormaPomocy",
  "d3Row2WartoscBrutto",
  "d3Row3DzienUdzielenia",
  "d3Row3PodmiotUdzielajacy",
  "d3Row3PodstawaPrawna",
  "d3Row3Przeznaczenie",
  "d3Row3FormaPomocy",
  "d3Row3WartoscBrutto",
  "d3Row4DzienUdzielenia",
  "d3Row4PodmiotUdzielajacy",
  "d3Row4PodstawaPrawna",
  "d3Row4Przeznaczenie",
  "d3Row4FormaPomocy",
  "d3Row4WartoscBrutto",
  // Section D.4 points b-h
  "d4bKoszty",
  "d4cIntensywnosc",
  "d4dIntensywnoscUdzielona",
  "d4eLokalizacja",
  "d4fCele",
  "d4gEtapy",
  "d4hDaty",
];

/**
 * Builds CSV template with multiple example rows showing all valid enum values.
 *
 * Row count = max enum cardinality = 6 (formaPrawna has 6 values).
 * Each enum column cycles through its valid values across rows.
 * Binary fields cycle: tak, nie, tak, nie, ...
 * Tri-state fields cycle: tak, nie, nie dotyczy, tak, nie, nie dotyczy
 */
function buildCsvTemplate(): string {
  const FORMA_VALUES = [
    "przedsiebiorstwo_panstwowe",
    "jednoosobowa_sp_skarbu",
    "jednoosobowa_jst",
    "podmiot_dominujacy",
    "sektor_finansow_publicznych",
    "inna",
  ];
  const WIELKOSC_VALUES = ["mikro", "maly", "sredni", "inny"];
  const PKD_WERSJA_VALUES = ["2007", "2024"];
  const BINARY_VALUES = ["tak", "nie"];
  const TRI_VALUES = ["tak", "nie", "nie dotyczy"];

  const pick = (arr: string[], i: number) => arr[i % arr.length];
  const d3Empty = new Array(24).fill("");

  const rows: string[][] = [];
  for (let i = 0; i < FORMA_VALUES.length; i++) {
    const b = pick(BINARY_VALUES, i);
    const t = pick(TRI_VALUES, i);
    rows.push([
      `000000000${i}`, // nip
      `Firma Przykładowa ${i + 1}`,
      `"ul. Testowa ${i + 1}, 00-00${i + 1} Warszawa"`, // adres (quoted, has comma)
      "1465011", // identyfikatorGminy
      FORMA_VALUES[i], // formaPrawna
      FORMA_VALUES[i] === "inna" ? "Spółka z o.o." : "", // formaPrawnaInna
      pick(WIELKOSC_VALUES, i), // wielkoscPodmiotu
      "62.01", // pkd
      pick(PKD_WERSJA_VALUES, i), // pkdWersja
      "15-03-2020", // dataUtworzenia
      "16-03-2026", // data
      "Specjalista", // stanowisko
      "+48 123 456 789", // telefon
      "Jan Kowalski", // imieNazwisko
      "", "", "", // nipWnioskodawcy, nazwaWnioskodawcy, adresWnioskodawcy
      b, b, b, b, b, // sekcja9a-e
      "", "", // sekcja9 conditional
      b, b, b, b, // sekcja10a-d
      "", "", // sekcja10ab conditional
      "", "", "", "", "", // sekcja10cd conditional
      b, // sekcjaB1
      t, // sekcjaB2 (tri-state)
      b, b, b, b, b, b, b, b, b, // sekcjaB3a-i
      "", // sekcjaB3iOpis
      b, b, b, // sekcjaC1a, C1b, C2
      t, // sekcjaC3 (tri-state)
      "", // sekcjaC3Opis
      b, // sekcjaD1
      t, // sekcjaD2 (tri-state)
      "", // opisPrzedsiewziecia
      ...d3Empty, // D.3 table (4 rows x 6 = 24 empty)
      "", "", "", "", "", "", "", // d4b-h
    ]);
  }

  return [CSV_HEADERS.join(","), ...rows.map((r) => r.join(","))].join("\n");
}

const CSV_TEMPLATE = buildCsvTemplate();

const VALID_FORMA_PRAWNA = [
  "przedsiebiorstwo_panstwowe",
  "jednoosobowa_sp_skarbu",
  "jednoosobowa_jst",
  "podmiot_dominujacy",
  "sektor_finansow_publicznych",
  "inna",
];

const VALID_WIELKOSC = ["mikro", "maly", "sredni", "inny"];

/** Fields that accept binary (tak/nie) values in CSV */
const BINARY_FIELDS = [
  "sekcja9a",
  "sekcja9b",
  "sekcja9c",
  "sekcja9d",
  "sekcja9e",
  "sekcja10a",
  "sekcja10b",
  "sekcja10c",
  "sekcja10d",
  "sekcjaB1",
  "sekcjaB3a",
  "sekcjaB3b",
  "sekcjaB3c",
  "sekcjaB3d",
  "sekcjaB3e",
  "sekcjaB3f",
  "sekcjaB3g",
  "sekcjaB3h",
  "sekcjaB3i",
  "sekcjaC1a",
  "sekcjaC1b",
  "sekcjaC2",
  "sekcjaD1",
] as const;

/** Fields that accept tri-state (tak/nie/nie dotyczy) values in CSV */
const TRI_STATE_FIELDS = ["sekcjaB2", "sekcjaC3", "sekcjaD2"] as const;

const VALID_BINARY_VALUES = ["tak", "nie"];
const VALID_TRI_STATE_VALUES = ["tak", "nie", "nie dotyczy", "nie_dotyczy"];
const VALID_PKD_WERSJA = ["2024", "2007"];

interface ParsedRow {
  nip: string;
  nazwa: string;
  adres: string;
  identyfikatorGminy: string;
  formaPrawna: string;
  formaPrawnaInna?: string;
  wielkoscPodmiotu: string;
  pkd: string;
  pkdWersja?: string;
  dataUtworzenia: string;
  data: string;
  stanowisko: string;
  telefon: string;
  imieNazwisko: string;
  // Page 1 right column (wnioskodawca)
  nipWnioskodawcy?: string;
  nazwaWnioskodawcy?: string;
  adresWnioskodawcy?: string;
  // Section A.9
  sekcja9a?: string;
  sekcja9b?: string;
  sekcja9c?: string;
  sekcja9d?: string;
  sekcja9e?: string;
  // Section A.9 conditional
  sekcja9NipPowiazanych?: string;
  sekcja9WartoscPomocy?: string;
  // Section A.10
  sekcja10a?: string;
  sekcja10b?: string;
  sekcja10c?: string;
  sekcja10d?: string;
  // Section A.10a/b conditional
  sekcja10abNipPolaczonych?: string;
  sekcja10abWartoscPomocy?: string;
  // Section A.10c/d conditional
  sekcja10cdNipPrzedPodzialem?: string;
  sekcja10cdWartoscPomocy?: string;
  sekcja10cdWartoscPrzedPodzialem?: string;
  sekcja10cdKapitalPrzed?: string;
  sekcja10cdKapitalMoment?: string;
  // Section B
  sekcjaB1?: string;
  sekcjaB2?: string;
  sekcjaB3a?: string;
  sekcjaB3b?: string;
  sekcjaB3c?: string;
  sekcjaB3d?: string;
  sekcjaB3e?: string;
  sekcjaB3f?: string;
  sekcjaB3g?: string;
  sekcjaB3h?: string;
  sekcjaB3i?: string;
  // Section B.3i conditional
  sekcjaB3iOpis?: string;
  // Section C
  sekcjaC1a?: string;
  sekcjaC1b?: string;
  sekcjaC2?: string;
  sekcjaC3?: string;
  // Section C.3 conditional
  sekcjaC3Opis?: string;
  // Section D
  sekcjaD1?: string;
  sekcjaD2?: string;
  // Section D.4a
  opisPrzedsiewziecia?: string;
  // Section D.4 points b-h
  d4bKoszty?: string;
  d4cIntensywnosc?: string;
  d4dIntensywnoscUdzielona?: string;
  d4eLokalizacja?: string;
  d4fCele?: string;
  d4gEtapy?: string;
  d4hDaty?: string;
  // Section D.3 table rows
  d3Row1DzienUdzielenia?: string;
  d3Row1PodmiotUdzielajacy?: string;
  d3Row1PodstawaPrawna?: string;
  d3Row1Przeznaczenie?: string;
  d3Row1FormaPomocy?: string;
  d3Row1WartoscBrutto?: string;
  d3Row2DzienUdzielenia?: string;
  d3Row2PodmiotUdzielajacy?: string;
  d3Row2PodstawaPrawna?: string;
  d3Row2Przeznaczenie?: string;
  d3Row2FormaPomocy?: string;
  d3Row2WartoscBrutto?: string;
  d3Row3DzienUdzielenia?: string;
  d3Row3PodmiotUdzielajacy?: string;
  d3Row3PodstawaPrawna?: string;
  d3Row3Przeznaczenie?: string;
  d3Row3FormaPomocy?: string;
  d3Row3WartoscBrutto?: string;
  d3Row4DzienUdzielenia?: string;
  d3Row4PodmiotUdzielajacy?: string;
  d3Row4PodstawaPrawna?: string;
  d3Row4Przeznaczenie?: string;
  d3Row4FormaPomocy?: string;
  d3Row4WartoscBrutto?: string;
}

type RowErrors = Record<string, string>;

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      result.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function parseCsv(text: string): ParsedRow[] {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]).map((h) => h.trim());
  const rows: ParsedRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const values = parseCsvLine(line);
    const record: Record<string, string> = {};
    headers.forEach((h, idx) => {
      record[h] = (values[idx] || "").trim();
    });
    rows.push(record as unknown as ParsedRow);
  }
  return rows;
}

/** Flexible date: D/M/YYYY, DD-MM-YYYY, YYYY-MM-DD etc. */
const FLEXIBLE_DATE = /^\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4}$/;

function validateRow(row: ParsedRow): RowErrors {
  const errors: RowErrors = {};

  if (!row.nip) errors.nip = "Wymagane";
  else if (!/^\d{10}$/.test(row.nip)) errors.nip = "NIP: dokładnie 10 cyfr";

  if (!row.nazwa) errors.nazwa = "Wymagane";

  if (!row.adres) errors.adres = "Wymagane";

  if (!row.identyfikatorGminy) errors.identyfikatorGminy = "Wymagane";
  else if (!/^\d{6,7}$/.test(row.identyfikatorGminy))
    errors.identyfikatorGminy = "TERYT: 6-7 cyfr";

  if (!row.formaPrawna) errors.formaPrawna = "Wymagane";
  else if (!VALID_FORMA_PRAWNA.includes(row.formaPrawna))
    errors.formaPrawna = `Dozwolone: ${VALID_FORMA_PRAWNA.join(", ")}`;

  if (!row.wielkoscPodmiotu) errors.wielkoscPodmiotu = "Wymagane";
  else if (!VALID_WIELKOSC.includes(row.wielkoscPodmiotu))
    errors.wielkoscPodmiotu = `Dozwolone: ${VALID_WIELKOSC.join(", ")}`;

  if (!row.pkd) errors.pkd = "Wymagane";

  if (row.pkdWersja && !VALID_PKD_WERSJA.includes(row.pkdWersja))
    errors.pkdWersja = `Dozwolone: ${VALID_PKD_WERSJA.join(", ")}`;

  if (!row.dataUtworzenia) errors.dataUtworzenia = "Wymagane";
  else if (!FLEXIBLE_DATE.test(row.dataUtworzenia))
    errors.dataUtworzenia = "Np. 15-03-2020 lub 6/5/2017";

  if (!row.data) errors.data = "Wymagane";
  else if (!FLEXIBLE_DATE.test(row.data))
    errors.data = "Np. 16-03-2026 lub 6/5/2017";

  if (!row.stanowisko) errors.stanowisko = "Wymagane";
  if (!row.telefon) errors.telefon = "Wymagane";
  if (!row.imieNazwisko) errors.imieNazwisko = "Wymagane";

  // Validate binary checkbox fields (optional - only validate if provided)
  for (const field of BINARY_FIELDS) {
    const value = row[field];
    if (value && !VALID_BINARY_VALUES.includes(value.toLowerCase()))
      errors[field] = "Dozwolone: tak, nie";
  }

  // Validate tri-state fields (optional - only validate if provided)
  for (const field of TRI_STATE_FIELDS) {
    const value = row[field];
    if (value && !VALID_TRI_STATE_VALUES.includes(value.toLowerCase()))
      errors[field] = "Dozwolone: tak, nie, nie dotyczy";
  }

  return errors;
}

const FIELD_LABELS: Record<string, string> = {
  nip: "NIP",
  nazwa: "Nazwa",
  adres: "Adres",
  identyfikatorGminy: "TERYT gminy",
  formaPrawna: "Forma prawna",
  wielkoscPodmiotu: "Wielkość",
  pkd: "PKD",
  pkdWersja: "PKD wersja",
  dataUtworzenia: "Data utworzenia",
  data: "Data formularza",
  stanowisko: "Stanowisko",
  telefon: "Telefon",
  imieNazwisko: "Imię i nazwisko",
  nipWnioskodawcy: "NIP wnioskodawcy",
  nazwaWnioskodawcy: "Nazwa wnioskodawcy",
  adresWnioskodawcy: "Adres wnioskodawcy",
  sekcja9a: "Sekcja 9a",
  sekcja9b: "Sekcja 9b",
  sekcja9c: "Sekcja 9c",
  sekcja9d: "Sekcja 9d",
  sekcja9e: "Sekcja 9e",
  sekcja9NipPowiazanych: "NIP powiązanych",
  sekcja9WartoscPomocy: "Wartość pomocy (sek. 9)",
  sekcja10a: "Sekcja 10a",
  sekcja10b: "Sekcja 10b",
  sekcja10c: "Sekcja 10c",
  sekcja10d: "Sekcja 10d",
  sekcja10abNipPolaczonych: "NIP połączonych (10a/b)",
  sekcja10abWartoscPomocy: "Wartość pomocy (10a/b)",
  sekcja10cdNipPrzedPodzialem: "NIP przed podziałem (10c/d)",
  sekcja10cdWartoscPomocy: "Wartość pomocy (10c/d)",
  sekcja10cdWartoscPrzedPodzialem: "Wartość pomocy przed podziałem",
  sekcja10cdKapitalPrzed: "Kapitał przed podziałem",
  sekcja10cdKapitalMoment: "Kapitał na moment podziału",
  sekcjaB1: "Sekcja B1",
  sekcjaB2: "Sekcja B2",
  sekcjaB3a: "Sekcja B3a",
  sekcjaB3b: "Sekcja B3b",
  sekcjaB3c: "Sekcja B3c",
  sekcjaB3d: "Sekcja B3d",
  sekcjaB3e: "Sekcja B3e",
  sekcjaB3f: "Sekcja B3f",
  sekcjaB3g: "Sekcja B3g",
  sekcjaB3h: "Sekcja B3h",
  sekcjaB3i: "Sekcja B3i",
  sekcjaB3iOpis: "Opis B3i",
  sekcjaC1a: "Sekcja C1a",
  sekcjaC1b: "Sekcja C1b",
  sekcjaC2: "Sekcja C2",
  sekcjaC3: "Sekcja C3",
  sekcjaC3Opis: "Opis C3",
  sekcjaD1: "Sekcja D1",
  sekcjaD2: "Sekcja D2",
  opisPrzedsiewziecia: "Opis przedsięwzięcia",
  d4bKoszty: "D.4b Koszty",
  d4cIntensywnosc: "D.4c Intensywność",
  d4dIntensywnoscUdzielona: "D.4d Intensywność udzielona",
  d4eLokalizacja: "D.4e Lokalizacja",
  d4fCele: "D.4f Cele",
  d4gEtapy: "D.4g Etapy",
  d4hDaty: "D.4h Daty",
  d3Row1DzienUdzielenia: "D.3 wiersz 1 - dzien",
  d3Row1PodmiotUdzielajacy: "D.3 wiersz 1 - podmiot",
  d3Row1PodstawaPrawna: "D.3 wiersz 1 - podstawa",
  d3Row1Przeznaczenie: "D.3 wiersz 1 - przeznaczenie",
  d3Row1FormaPomocy: "D.3 wiersz 1 - forma",
  d3Row1WartoscBrutto: "D.3 wiersz 1 - wartosc",
  d3Row2DzienUdzielenia: "D.3 wiersz 2 - dzien",
  d3Row2PodmiotUdzielajacy: "D.3 wiersz 2 - podmiot",
  d3Row2PodstawaPrawna: "D.3 wiersz 2 - podstawa",
  d3Row2Przeznaczenie: "D.3 wiersz 2 - przeznaczenie",
  d3Row2FormaPomocy: "D.3 wiersz 2 - forma",
  d3Row2WartoscBrutto: "D.3 wiersz 2 - wartosc",
  d3Row3DzienUdzielenia: "D.3 wiersz 3 - dzien",
  d3Row3PodmiotUdzielajacy: "D.3 wiersz 3 - podmiot",
  d3Row3PodstawaPrawna: "D.3 wiersz 3 - podstawa",
  d3Row3Przeznaczenie: "D.3 wiersz 3 - przeznaczenie",
  d3Row3FormaPomocy: "D.3 wiersz 3 - forma",
  d3Row3WartoscBrutto: "D.3 wiersz 3 - wartosc",
  d3Row4DzienUdzielenia: "D.3 wiersz 4 - dzien",
  d3Row4PodmiotUdzielajacy: "D.3 wiersz 4 - podmiot",
  d3Row4PodstawaPrawna: "D.3 wiersz 4 - podstawa",
  d3Row4Przeznaczenie: "D.3 wiersz 4 - przeznaczenie",
  d3Row4FormaPomocy: "D.3 wiersz 4 - forma",
  d3Row4WartoscBrutto: "D.3 wiersz 4 - wartosc",
};

// ---------------------------------------------------------------------------
// Edit-in-form helpers
// ---------------------------------------------------------------------------

function getStorageKey(tenantId: string | null): string {
  return `de-minimis-form-data-v1-${tenantId || "default"}`;
}

/** Convert a CSV ParsedRow into the DeMinimisFormState shape used by GrantsFormsInteractive */
function parsedRowToFormState(row: ParsedRow) {
  // Helper: CSV "tak"/"nie" -> boolean
  const toBool = (v: string | undefined): boolean =>
    (v || "").toLowerCase() === "tak";

  // Helper: CSV tri-state -> normalized string value
  const toTriState = (v: string | undefined): string => {
    const lower = (v || "").toLowerCase();
    if (lower === "tak") return "tak";
    if (lower === "nie") return "nie";
    if (lower === "nie dotyczy" || lower === "nie_dotyczy") return "nie_dotyczy";
    return "nie_dotyczy";
  };

  // Build d3Rows (max 4 rows for the form)
  const d3Rows: Array<{
    dzienUdzielenia: string;
    podmiotUdzielajacy: string;
    podstawaPrawna: string;
    przeznaczenie: string;
    formaPomocy: string;
    wartoscBrutto: string;
  }> = [];
  for (let i = 1; i <= 4; i++) {
    const dzien = row[`d3Row${i}DzienUdzielenia` as keyof ParsedRow] || "";
    const podmiot = row[`d3Row${i}PodmiotUdzielajacy` as keyof ParsedRow] || "";
    const podstawa = row[`d3Row${i}PodstawaPrawna` as keyof ParsedRow] || "";
    const przeznaczenie =
      row[`d3Row${i}Przeznaczenie` as keyof ParsedRow] || "";
    const forma = row[`d3Row${i}FormaPomocy` as keyof ParsedRow] || "";
    const wartosc = row[`d3Row${i}WartoscBrutto` as keyof ParsedRow] || "";
    d3Rows.push({
      dzienUdzielenia: dzien,
      podmiotUdzielajacy: podmiot,
      podstawaPrawna: podstawa,
      przeznaczenie,
      formaPomocy: forma,
      wartoscBrutto: wartosc,
    });
  }
  // Pad to 4 rows
  while (d3Rows.length < 4) {
    d3Rows.push({
      dzienUdzielenia: "",
      podmiotUdzielajacy: "",
      podstawaPrawna: "",
      przeznaczenie: "",
      formaPomocy: "",
      wartoscBrutto: "",
    });
  }

  return {
    // Section A
    nip: row.nip || "",
    nazwa: row.nazwa || "",
    adres: row.adres || "",
    identyfikatorGminy: row.identyfikatorGminy || "",
    formaPrawna: row.formaPrawna || "",
    formaPrawnaInna: row.formaPrawnaInna || "",
    wielkoscPodmiotu: row.wielkoscPodmiotu || "",
    pkd: row.pkd || "",
    pkdWersja: row.pkdWersja || "2007",
    dataUtworzenia: row.dataUtworzenia || "",
    // Page 1 right column (wnioskodawca)
    nipWnioskodawcy: row.nipWnioskodawcy || "",
    nazwaWnioskodawcy: row.nazwaWnioskodawcy || "",
    adresWnioskodawcy: row.adresWnioskodawcy || "",
    // Section A.9 - binary
    sekcja9a: toBool(row.sekcja9a),
    sekcja9b: toBool(row.sekcja9b),
    sekcja9c: toBool(row.sekcja9c),
    sekcja9d: toBool(row.sekcja9d),
    sekcja9e: toBool(row.sekcja9e),
    // Section A.9 conditional
    sekcja9NipPowiazanych: row.sekcja9NipPowiazanych || "",
    sekcja9WartoscPomocy: row.sekcja9WartoscPomocy || "",
    // Section A.10 - binary
    sekcja10a: toBool(row.sekcja10a),
    sekcja10b: toBool(row.sekcja10b),
    sekcja10c: toBool(row.sekcja10c),
    sekcja10d: toBool(row.sekcja10d),
    // Section A.10a/b conditional
    sekcja10abNipPolaczonych: row.sekcja10abNipPolaczonych || "",
    sekcja10abWartoscPomocy: row.sekcja10abWartoscPomocy || "",
    // Section A.10c/d conditional
    sekcja10cdNipPrzedPodzialem: row.sekcja10cdNipPrzedPodzialem || "",
    sekcja10cdWartoscPomocy: row.sekcja10cdWartoscPomocy || "",
    sekcja10cdWartoscPrzedPodzialem: row.sekcja10cdWartoscPrzedPodzialem || "",
    sekcja10cdKapitalPrzed: row.sekcja10cdKapitalPrzed || "",
    sekcja10cdKapitalMoment: row.sekcja10cdKapitalMoment || "",
    // Section B
    sekcjaB1: toBool(row.sekcjaB1),
    sekcjaB2: toTriState(row.sekcjaB2),
    sekcjaB3a: toBool(row.sekcjaB3a),
    sekcjaB3b: toBool(row.sekcjaB3b),
    sekcjaB3c: toBool(row.sekcjaB3c),
    sekcjaB3d: toBool(row.sekcjaB3d),
    sekcjaB3e: toBool(row.sekcjaB3e),
    sekcjaB3f: toBool(row.sekcjaB3f),
    sekcjaB3g: toBool(row.sekcjaB3g),
    sekcjaB3h: toBool(row.sekcjaB3h),
    sekcjaB3i: toBool(row.sekcjaB3i),
    // Section B.3i conditional
    sekcjaB3iOpis: row.sekcjaB3iOpis || "",
    // Section C
    sekcjaC1a: toBool(row.sekcjaC1a),
    sekcjaC1b: toBool(row.sekcjaC1b),
    sekcjaC2: toBool(row.sekcjaC2),
    sekcjaC3: toTriState(row.sekcjaC3),
    // Section C.3 conditional
    sekcjaC3Opis: row.sekcjaC3Opis || "",
    // Section D
    sekcjaD1: toBool(row.sekcjaD1),
    sekcjaD2: toTriState(row.sekcjaD2),
    // Section D.4a
    opisPrzedsiewziecia: row.opisPrzedsiewziecia || "",
    // Section D.4 points b-h
    d4bKoszty: row.d4bKoszty || "",
    d4cIntensywnosc: row.d4cIntensywnosc || "",
    d4dIntensywnoscUdzielona: row.d4dIntensywnoscUdzielona || "",
    d4eLokalizacja: row.d4eLokalizacja || "",
    d4fCele: row.d4fCele || "",
    d4gEtapy: row.d4gEtapy || "",
    d4hDaty: row.d4hDaty || "",
    // Section D.3 - Table of received aid
    d3Rows,
    // Section E
    data: row.data || "",
    stanowisko: row.stanowisko || "",
    telefon: row.telefon || "",
    imieNazwisko: row.imieNazwisko || "",
  };
}

const GrantsFormsCsvImport: React.FC = () => {
  const navigate = useNavigate();
  const { selectedTenantId } = useHotel();

  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const rowErrors = useMemo(() => rows.map(validateRow), [rows]);
  const hasErrors = useMemo(
    () => rowErrors.some((e) => Object.keys(e).length > 0),
    [rowErrors]
  );
  const errorCount = useMemo(
    () => rowErrors.filter((e) => Object.keys(e).length > 0).length,
    [rowErrors]
  );

  const handleDownloadTemplate = useCallback(() => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
    downloadCsvBlob(blob, "de-minimis-szablon-kiedynabory.csv");
  }, []);

  const handleFile = useCallback((file: File) => {
    setError(null);
    if (!file.name.endsWith(".csv")) {
      setError("Plik musi mieć rozszerzenie .csv");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const parsed = parseCsv(text);
        if (parsed.length === 0) {
          setError("Plik CSV jest pusty lub nie zawiera danych");
          return;
        }
        if (parsed.length > 200) {
          setError("Maksymalnie 200 wierszy");
          return;
        }
        setRows(parsed);
        setFileName(file.name);
      } catch {
        setError("Nie udało się odczytać pliku CSV");
      }
    };
    reader.readAsText(file, "utf-8");
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = "";
    },
    [handleFile]
  );

  const handleGenerate = useCallback(async () => {
    if (rows.length === 0 || hasErrors) return;
    setIsGenerating(true);
    setError(null);

    try {
      // Map CSV string values to DTO types expected by the backend
      const mappedRows = rows.map((row) => {
        const mapped: Record<string, unknown> = {
          nip: row.nip,
          nazwa: row.nazwa,
          adres: row.adres,
          identyfikatorGminy: row.identyfikatorGminy,
          formaPrawna: row.formaPrawna,
          formaPrawnaInna: row.formaPrawnaInna || undefined,
          wielkoscPodmiotu: row.wielkoscPodmiotu,
          pkd: row.pkd,
          dataUtworzenia: row.dataUtworzenia,
          data: row.data,
          stanowisko: row.stanowisko,
          telefon: row.telefon,
          imieNazwisko: row.imieNazwisko,
        };

        // pkdWersja: string -> string enum or undefined
        if (row.pkdWersja) mapped.pkdWersja = row.pkdWersja;

        // Binary fields: "tak" -> true, "nie" -> false, empty -> undefined
        for (const field of BINARY_FIELDS) {
          const value = row[field];
          if (value) {
            mapped[field] = value.toLowerCase() === "tak";
          }
        }

        // Tri-state fields: "tak" -> "tak", "nie" -> "nie", "nie dotyczy"/"nie_dotyczy" -> "nie_dotyczy", empty -> undefined
        for (const field of TRI_STATE_FIELDS) {
          const value = row[field];
          if (value) {
            const lower = value.toLowerCase();
            if (lower === "tak") mapped[field] = "tak";
            else if (lower === "nie") mapped[field] = "nie";
            else if (lower === "nie dotyczy" || lower === "nie_dotyczy")
              mapped[field] = "nie_dotyczy";
          }
        }

        // Optional text fields: pass through if non-empty
        const optionalTextFields = [
          "nipWnioskodawcy",
          "nazwaWnioskodawcy",
          "adresWnioskodawcy",
          "sekcja9NipPowiazanych",
          "sekcja9WartoscPomocy",
          "sekcja10abNipPolaczonych",
          "sekcja10abWartoscPomocy",
          "sekcja10cdNipPrzedPodzialem",
          "sekcja10cdWartoscPomocy",
          "sekcja10cdWartoscPrzedPodzialem",
          "sekcja10cdKapitalPrzed",
          "sekcja10cdKapitalMoment",
          "sekcjaB3iOpis",
          "sekcjaC3Opis",
          "opisPrzedsiewziecia",
          "d4bKoszty",
          "d4cIntensywnosc",
          "d4dIntensywnoscUdzielona",
          "d4eLokalizacja",
          "d4fCele",
          "d4gEtapy",
          "d4hDaty",
        ] as const;
        for (const field of optionalTextFields) {
          const value = row[field as keyof ParsedRow];
          if (value) mapped[field] = value;
        }

        // D.3 table rows - map flat CSV columns to d3Rows array
        const d3Rows: Array<Record<string, string>> = [];
        for (let i = 1; i <= 4; i++) {
          const dzien = row[`d3Row${i}DzienUdzielenia` as keyof ParsedRow] || "";
          const podmiot = row[`d3Row${i}PodmiotUdzielajacy` as keyof ParsedRow] || "";
          const podstawa = row[`d3Row${i}PodstawaPrawna` as keyof ParsedRow] || "";
          const przeznaczenie = row[`d3Row${i}Przeznaczenie` as keyof ParsedRow] || "";
          const forma = row[`d3Row${i}FormaPomocy` as keyof ParsedRow] || "";
          const wartosc = row[`d3Row${i}WartoscBrutto` as keyof ParsedRow] || "";
          // Only include non-empty rows
          if (dzien || podmiot || podstawa || przeznaczenie || forma || wartosc) {
            const d3Row: Record<string, string> = {};
            if (dzien) d3Row.dzienUdzielenia = dzien;
            if (podmiot) d3Row.podmiotUdzielajacy = podmiot;
            if (podstawa) d3Row.podstawaPrawna = podstawa;
            if (przeznaczenie) d3Row.przeznaczenie = przeznaczenie;
            if (forma) d3Row.formaPomocy = forma;
            if (wartosc) d3Row.wartoscBrutto = wartosc;
            d3Rows.push(d3Row);
          }
        }
        if (d3Rows.length > 0) {
          mapped.d3Rows = d3Rows;
        }

        return mapped;
      });

      const { data } = await apiClient.post(
        "/grants/forms/de-minimis/generate",
        { rows: mappedRows },
        { responseType: "blob" }
      );

      const nips = rows.map((r) => r.nip).filter(Boolean);
      const zipName =
        nips.length === 1
          ? `de-minimis-${nips[0]}.zip`
          : "de-minimis-formularze.zip";

      downloadCsvBlob(new Blob([data]), zipName);
    } catch (err: unknown) {
      if (err && typeof err === "object" && "response" in err) {
        const resp = (err as { response: { status: number; data: Blob } }).response;
        if (resp.status === 429) {
          setError("Przekroczono limit generowań (3/min). Odczekaj chwilę.");
        } else if (resp.status === 422) {
          try {
            const text = await resp.data.text();
            const json = JSON.parse(text);
            const messages = json.message;
            setError(
              Array.isArray(messages) ? messages.join("\n") : String(messages)
            );
          } catch {
            setError("Błąd walidacji danych formularza");
          }
        } else {
          try {
            const text = await resp.data.text();
            const json = JSON.parse(text);
            const messages = json.message;
            setError(
              Array.isArray(messages) ? messages.join("\n") : String(messages)
            );
          } catch {
            setError("Błąd serwera podczas generowania formularzy");
          }
        }
      } else {
        setError("Nie udało się połączyć z serwerem");
      }
    } finally {
      setIsGenerating(false);
    }
  }, [rows, hasErrors]);

  const handleClear = useCallback(() => {
    setRows([]);
    setFileName("");
    setError(null);
  }, []);

  const handleEditInForm = useCallback(
    (row: ParsedRow) => {
      const storageKey = getStorageKey(selectedTenantId);
      const existing = localStorage.getItem(storageKey);
      if (existing) {
        try {
          const parsed = JSON.parse(existing);
          if (parsed.nip || parsed.nazwa) {
            const confirmed = window.confirm(
              "Formularz zawiera niezapisane dane. Czy chcesz je nadpisać?"
            );
            if (!confirmed) return;
          }
        } catch {
          // Malformed data — safe to overwrite
        }
      }
      const formState = parsedRowToFormState(row);
      localStorage.setItem(storageKey, JSON.stringify(formState));
      navigate("/grants/forms");
    },
    [selectedTenantId, navigate]
  );

  return (
    <Layout title="Formularze de minimis - Import CSV">
      <div className="space-y-6">
        {/* Template download */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            1. Pobierz szablon CSV
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Pobierz szablon, wypełnij danymi firm i załaduj poniżej.
          </p>
          <button
            onClick={handleDownloadTemplate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <DownloadIcon className="w-4 h-4" />
            Pobierz szablon CSV
          </button>
        </div>

        {/* File upload */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            2. Załaduj wypełniony CSV
          </h2>

          {rows.length === 0 ? (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? "border-primary-400 bg-primary-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              role="button"
              tabIndex={0}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() =>
                document.getElementById("csv-file-input")?.click()
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  document.getElementById("csv-file-input")?.click();
                }
              }}
            >
              <CloudUploadIcon className="w-10 h-10 mx-auto text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 mb-1">
                Przeciągnij plik CSV lub kliknij, żeby wybrać
              </p>
              <p className="text-xs text-gray-400">Maks. 200 wierszy</p>
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileInput}
              />
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-700">
                  <span className="font-medium">{fileName}</span>
                  <span className="text-gray-500 ml-2">
                    ({rows.length}{" "}
                    {rows.length === 1
                      ? "firma"
                      : rows.length < 5
                        ? "firmy"
                        : "firm"}
                    )
                  </span>
                  {hasErrors && (
                    <span className="text-red-600 ml-2">
                      {errorCount}{" "}
                      {errorCount === 1
                        ? "wiersz z błędami"
                        : errorCount < 5
                          ? "wiersze z błędami"
                          : "wierszy z błędami"}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleClear}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Usuń
                </button>
              </div>

              {/* Preview table */}
              <div className="overflow-x-auto border border-gray-200 rounded-md max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">
                        #
                      </th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">
                        NIP
                      </th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">
                        Nazwa
                      </th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">
                        TERYT
                      </th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">
                        PKD
                      </th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">
                        Data utw.
                      </th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">
                        Błędy
                      </th>
                      <th className="px-3 py-2 text-center text-gray-500 font-medium">
                        Edytuj
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const errs = rowErrors[i];
                      const errKeys = Object.keys(errs);
                      const hasRowErrors = errKeys.length > 0;
                      return (
                        <tr
                          key={i}
                          className={`border-t ${hasRowErrors ? "bg-red-50" : "hover:bg-gray-50"}`}
                        >
                          <td className="px-3 py-1.5 text-gray-400">
                            {i + 1}
                          </td>
                          <td
                            className={`px-3 py-1.5 font-mono ${errs.nip ? "text-red-600" : ""}`}
                          >
                            {row.nip || "—"}
                          </td>
                          <td className="px-3 py-1.5 max-w-[200px] truncate">
                            {row.nazwa || "—"}
                          </td>
                          <td
                            className={`px-3 py-1.5 font-mono ${errs.identyfikatorGminy ? "text-red-600" : ""}`}
                          >
                            {row.identyfikatorGminy || "—"}
                          </td>
                          <td className="px-3 py-1.5">{row.pkd || "—"}</td>
                          <td
                            className={`px-3 py-1.5 ${errs.dataUtworzenia ? "text-red-600" : ""}`}
                          >
                            {row.dataUtworzenia || "—"}
                          </td>
                          <td className="px-3 py-1.5">
                            {hasRowErrors ? (
                              <span
                                className="text-red-600 cursor-help"
                                title={errKeys
                                  .map(
                                    (k) =>
                                      `${FIELD_LABELS[k] || k}: ${errs[k]}`
                                  )
                                  .join("\n")}
                              >
                                {errKeys.map((k) => FIELD_LABELS[k] || k).join(", ")}
                              </span>
                            ) : (
                              <span className="text-green-600">OK</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <button
                              onClick={() => handleEditInForm(row)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-gray-500 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
                              title="Edytuj w formularzu"
                            >
                              <EditIcon className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Generate */}
        {rows.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              3. Generuj formularze
            </h2>
            {hasErrors && (
              <p className="text-sm text-red-600 mb-3">
                Popraw błędy w CSV przed generowaniem.
              </p>
            )}
            <button
              onClick={handleGenerate}
              disabled={isGenerating || hasErrors}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-700 text-white rounded-md text-sm font-medium hover:bg-primary-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="opacity-25"
                    />
                    <path
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      className="opacity-75"
                    />
                  </svg>
                  Generowanie...
                </>
              ) : (
                <>
                  <DownloadIcon className="w-4 h-4" />
                  Pobierz formularze (ZIP){rows.length > 1 ? ` (${rows.length})` : ""}
                </>
              )}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Paczka zawiera formularze w formacie PDF.
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700 whitespace-pre-line">{error}</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GrantsFormsCsvImport;
